<template>
    <button
        v-if="isDisplayed"
        ref="elemRef"
        class="tl-selection-list-tile"
        :class="itemClass"
        type="button"
        :disabled="disabled"
        @click="handleClick"
    >
        <div
            class="tl-selection-list-tile-grid"
            :class="{
                'tl-selection-list-tile-navigation-info': showToggle,
            }"
        >
            <Position
                v-if="showToggle"
                x="center"
                y="center"
                class="tl-selection-list-tile-narrow"
            >
                <CheckTrigger
                    tabindex="-1"
                    :model-value="item.navigationOption?.selected"
                />
            </Position>

            <Position
                v-if="item.icon"
                x="center"
                y="center"
                class="tl-selection-list-tile-narrow"
            >
                <img
                    class="tl-selection-list-tile-icon"
                    :src="item.icon"
                    alt="icon"
                />
            </Position>

            <Position
                :x="horizontal ? 'center' : 'start'"
                :y="horizontal ? 'start' : 'center'"
                class="tl-selection-list-tile-wide"
            >
                <p>
                    <Words title>{{ item.label }}</Words>
                    <Words
                        v-if="item.description"
                        paragraph
                        dense
                        twoline
                    >
                        {{ item.description }}
                    </Words>
                </p>
            </Position>

            <Position
                v-if="!item.hideRightIcon && !horizontal"
                x="end"
                y="center"
                class="tl-selection-list-tile-narrow"
                :class="{ 'tl-selection-list-tile-arrow': !item.rightIcon }"
            >
                <transition
                    name="scale"
                    mode="out-in"
                >
                    <img
                        v-if="item.rightIcon"
                        class="tl-selection-list-tile-icon"
                        :src="item.rightIcon"
                        alt="right-icon"
                    />
                    <ArrowRight
                        v-else
                        :size="10"
                    />
                </transition>
            </Position>
        </div>
    </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Position from 'widget-components/components/Position/Position.vue'
import Words from 'widget-components/components/Words/Words.vue'
import ArrowRight from 'widget-components/assets/svg/ArrowRight.vue'
import CheckTrigger from 'widget-components/components/InputCheckbox/CheckTrigger.vue'
import type { SelectionListItem } from 'widget-components/integrations/CustomPage/types'
import useConditions from 'widget-components/composables/conditions/useConditions'

const props = defineProps<{
    item: SelectionListItem
    loading?: boolean
    horizontal?: boolean
}>()

const itemId = computed(() => props.item.id || '')

const showToggle = computed(() =>
    props.item.navigationOption && !props.item.navigationOption.hideToggle)

const { isDisplayed, isDisabled } = useConditions(itemId)

const elemRef = ref<HTMLElement | undefined>()

const hasItemGap = computed(() => {
    if (!elemRef.value) {
        return false
    }

    const val = getComputedStyle(elemRef.value)
        .getPropertyValue('--tl-container-item-gap')
        .trim()

    if (!val || val === '0' || val === '0px') {
        return false
    }

    return true
})

const disabled = computed(() => props.item.navigationOption?.disabled || isDisabled.value)

const itemClass = computed(() => ({
    loading: props.loading,
    'tl-selection-list-tile-horizontal': props.horizontal,
    'tl-selection-list-tile-active': props.item.navigationOption?.active,
    'tl-selection-list-tile-disabled': disabled.value,
    'tl-selection-list-tile-pill': hasItemGap.value,
    'tl-selection-list-tile-with-toggle': showToggle.value,
}))

const emit = defineEmits(['click'])

const handleClick = () => {
    if (props.loading || disabled.value) return
    emit('click')
}
</script>

<style lang="sass" scoped>
@import './SelectionListTile.sass'
</style>
